import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHref } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronLeft, ChevronRight, Film, X, BarChart3, LayoutGrid, Table2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AnalyticsGalleryTab } from './AnalyticsGalleryTab';
import type { AnalyticsGalleryListItem } from './AnalyticsGalleryTab';
import XTAMSpinner from '../components/XTAMSpinner';
import './InventoryPage.css';
import './AnalyticsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

interface AnalyticsListItem {
  id: string;
  hostSerial: string;
  cameraExternalId: string;
  devicesCameraId: number | null;
  eventTsMs: number;
  eventTime: string;
  persons: number;
  vehicles: number;
  isCrowd: boolean;
  detectionCount: number;
  hasImage: boolean;
  siteId: number | null;
  siteName: string | null;
  cameraName: string | null;
}

interface AnalyticsDetail extends AnalyticsListItem {
  detections: Array<{
    id: string;
    trackId: number | null;
    label: string;
    labelRaw: string | null;
    objectType: string | null;
    color: string | null;
    score: number;
    bbox: { x1: number; y1: number; x2: number; y2: number };
  }>;
}

function countTone(n: number): 'low' | 'mid' | 'high' {
  if (n <= 5) return 'low';
  if (n <= 10) return 'mid';
  return 'high';
}

function formatLocal(dt: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch {
    return dt;
  }
}

function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Asegura dos strings válidos para datetime-local y un orden from ≤ to (evita Desde/Hasta vacíos o invertidos). Si falta "Desde", por defecto última hora respecto a "Hasta". */
function resolveAnalyticsRange(fromRaw: string, toRaw: string): {
  fromStr: string;
  toStr: string;
  corrected: boolean;
} {
  const parseLocal = (s: string): Date | null => {
    const t = s.trim();
    if (!t) return null;
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  let toD = parseLocal(toRaw);
  let fromD = parseLocal(fromRaw);

  let corrected = false;

  if (!toD) {
    toD = new Date();
    corrected = true;
  }
  if (!fromD) {
    fromD = new Date(toD.getTime() - 60 * 60 * 1000);
    corrected = true;
  }
  if (fromD.getTime() > toD.getTime()) {
    fromD = new Date(toD.getTime() - 60 * 60 * 1000);
    corrected = true;
  }

  const fromStr = toDatetimeLocalValue(fromD);
  const toStr = toDatetimeLocalValue(toD);
  if (!corrected && (fromStr !== fromRaw.trim() || toStr !== toRaw.trim())) {
    corrected = true;
  }

  return { fromStr, toStr, corrected };
}

const OBJECT_LABEL_ES: Record<string, string> = {
  truck: 'Carro',
  person: 'Persona',


};

const COLOR_LABEL_ES: Record<string, string> = {
  yellow: 'amarillo',
  black: 'negro',
  white: 'blanco',
  gray: 'gris',
  grey: 'gris',
  red: 'rojo',
  blue: 'azul',
  green: 'verde',
  orange: 'naranja',
  brown: 'marrón',
  silver: 'plateado',
};

function translateColorLabel(s: string): string {
  const k = s.trim().toLowerCase();
  return COLOR_LABEL_ES[k] ?? s;
}

function formatDetectionForUi(d: AnalyticsDetail['detections'][number]): string {
  const raw = d.labelRaw || d.label || '';
  const colon = raw.indexOf(':');
  const typePart = colon >= 0 ? raw.slice(0, colon).trim() : raw.trim();
  const colorFromLabel = colon >= 0 ? raw.slice(colon + 1).trim() : '';
  const tKey = (d.objectType || typePart || '').toLowerCase();
  const typeLabel =
    OBJECT_LABEL_ES[tKey] ||
    OBJECT_LABEL_ES[typePart.toLowerCase()] ||
    typePart ||
    d.label;
  const colorRaw = d.color || colorFromLabel;
  const colorLabel = colorRaw ? translateColorLabel(colorRaw) : '';
  const pct = Math.min(100, Math.max(0, Math.round(Number(d.score) * 100)));
  if (colorLabel) {
    return `${typeLabel} · ${colorLabel} · ${pct}% confianza`;
  }
  return `${typeLabel} · ${pct}% confianza`;
}

/** Alineado con GET /analytics/events sortBy (backend). */
type AnalyticsSortKey =
  | 'event_time'
  | 'site_name'
  | 'camera'
  | 'host_serial'
  | 'persons'
  | 'vehicles'
  | 'is_crowd';

function defaultSortDirForColumn(key: AnalyticsSortKey): 'asc' | 'desc' {
  if (key === 'site_name' || key === 'camera' || key === 'host_serial') return 'asc';
  return 'desc';
}

function recordingBlockedReason(row: {
  devicesCameraId: number | null;
  siteId: number | null;
}): string | null {
  if (row.siteId != null && row.devicesCameraId != null) return null;
  if (row.siteId == null && row.devicesCameraId == null) {
    return 'Sin sitio ni cámara vinculados en BD; no se puede abrir grabación.';
  }
  if (row.siteId == null) {
    return 'Sin sitio en el evento (site_id al ingest o resolución por cámara).';
  }
  return 'Sin cámara en devices_cameras para este canal; hace falta el registro de la cámara para playback.';
}

const AnalyticsPage: React.FC = () => {
  const { token } = useAuth();
  const recordingsHref = useHref('/recordings');
  const [from, setFrom] = useState(() =>
    toDatetimeLocalValue(new Date(Date.now() - 60 * 60 * 1000))
  );
  const [to, setTo] = useState(() => toDatetimeLocalValue(new Date()));
  const [siteName, setSiteName] = useState('');
  /** Valores alineados con backend vehicleColorPreset */
  const [vehicleColorPreset, setVehicleColorPreset] = useState('');
  const [sortBy, setSortBy] = useState<AnalyticsSortKey>('event_time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<AnalyticsListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AnalyticsDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  /** GET /analytics/events/:id en curso cuando ya mostramos fila del listado como vista previa. */
  const [detailApiPending, setDetailApiPending] = useState(false);
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  /** Imagen aún descargándose (detalle ya listo o en paralelo). */
  const [imageLoading, setImageLoading] = useState(false);
  /** objectURL por event id — reabrir el mismo evento evita otra descarga. */
  const imageBlobCacheRef = useRef<Map<string, string>>(new Map());
  const [analyticsSubTab, setAnalyticsSubTab] = useState<'table' | 'gallery'>('gallery');
  const [gallerySearchNonce, setGallerySearchNonce] = useState(0);

  const listParamsRef = useRef({
    from,
    to,
    siteName,
    vehicleColorPreset,
    sortBy,
    sortDir,
    page,
    limit,
  });
  listParamsRef.current = {
    from,
    to,
    siteName,
    vehicleColorPreset,
    sortBy,
    sortDir,
    page,
    limit,
  };

  const toIso = (localDatetimeLocal: string) => {
    const d = new Date(localDatetimeLocal);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const fetchList = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    const p = listParamsRef.current;
    const resolved = resolveAnalyticsRange(p.from, p.to);
    if (resolved.corrected) {
      setFrom(resolved.fromStr);
      setTo(resolved.toStr);
      return;
    }
    const fromIso = toIso(resolved.fromStr);
    const toIsoVal = toIso(resolved.toStr);
    if (!fromIso || !toIsoVal) {
      setError('Rango de fechas inválido');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        from: fromIso,
        to: toIsoVal,
        page: String(p.page),
        limit: String(p.limit),
        sortBy: p.sortBy,
        sortDir: p.sortDir,
      });
      if (p.siteName.trim()) params.set('siteName', p.siteName.trim());
      if (p.vehicleColorPreset.trim()) params.set('vehicleColorPreset', p.vehicleColorPreset.trim());

      const res = await axios.get(`${API_URL}/analytics/events?${params.toString()}`);
      const data = res.data?.data;
      setItems(data?.items ?? []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        // No dejar filas de un fetch anterior si el token ya no vale (logout/redirección va aparte).
        setItems([]);
        setTotal(0);
        setError(null);
        return;
      }
      let msg = 'No se pudieron cargar los eventos';
      if (axios.isAxiosError(e) && e.response?.data) {
        const d = e.response.data as {
          error?: string | { message?: string };
          message?: string;
        };
        if (typeof d.error === 'string') msg = d.error;
        else if (d.error && typeof d.error === 'object' && d.error.message)
          msg = String(d.error.message);
        else if (d.message) msg = String(d.message);
      }
      if (msg === 'An unexpected error occurred') {
        msg =
          'Error del servidor al cargar eventos. Reinicia o actualiza el contenedor del API (backend) e inténtalo de nuevo.';
      }
      setError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchList();
  }, [token, page, fetchList, from, to, sortBy, sortDir]);

  const onSortColumn = (key: AnalyticsSortKey) => {
    setPage(1);
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(defaultSortDirForColumn(key));
    }
  };

  const MAX_CACHED_EVENT_IMAGES = 20;

  const putImageInCache = useCallback((id: string, objectUrl: string) => {
    const m = imageBlobCacheRef.current;
    if (m.get(id) === objectUrl) return;
    if (m.has(id)) {
      URL.revokeObjectURL(m.get(id)!);
      m.delete(id);
    }
    while (m.size >= MAX_CACHED_EVENT_IMAGES) {
      const first = m.keys().next().value as string | undefined;
      if (!first) break;
      URL.revokeObjectURL(m.get(first)!);
      m.delete(first);
    }
    m.set(id, objectUrl);
  }, []);

  const getCachedEventImageUrl = useCallback(
    (eventId: string) => imageBlobCacheRef.current.get(eventId) ?? null,
    []
  );

  const registerEventImageUrl = useCallback(
    (eventId: string, objectUrl: string) => {
      putImageInCache(eventId, objectUrl);
    },
    [putImageInCache]
  );

  const openDetail = async (id: string, rowHint?: AnalyticsListItem) => {
    if (!localStorage.getItem('token')) return;

    const cached = imageBlobCacheRef.current.get(id);
    if (cached) {
      setImageObjectUrl(cached);
      setImageLoading(false);
    } else {
      setImageObjectUrl(null);
      setImageLoading(Boolean(rowHint?.hasImage));
    }

    const hasPlaceholder = rowHint != null;
    if (hasPlaceholder) {
      setDetail({ ...rowHint, detections: [] });
      setDetailLoading(false);
      setDetailApiPending(true);
    } else {
      setDetailLoading(true);
      setDetail(null);
      setDetailApiPending(false);
    }

    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

    const prefetchImage = Boolean(rowHint?.hasImage) && !cached;
    const imageReq = prefetchImage
      ? axios
          .get(`${API_URL}/analytics/events/${id}/image`, { responseType: 'blob', ...auth })
          .then((r) => r.data as Blob)
          .catch(() => null as Blob | null)
      : Promise.resolve<Blob | null>(null);

    try {
      const [detailRes, imageBlobEarly] = await Promise.all([
        axios.get(`${API_URL}/analytics/events/${id}`, auth),
        imageReq,
      ]);
      const detailData = (detailRes.data?.data ?? null) as AnalyticsDetail | null;
      setDetail(detailData);

      let blob: Blob | null = imageBlobEarly;
      if (detailData?.hasImage && !cached && !blob) {
        setImageLoading(true);
        try {
          const ir = await axios.get(`${API_URL}/analytics/events/${id}/image`, {
            responseType: 'blob',
            ...auth,
          });
          blob = ir.data;
        } catch {
          blob = null;
        }
      }

      if (detailData?.hasImage && blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
        putImageInCache(id, url);
        setImageObjectUrl(url);
      } else if (!detailData?.hasImage) {
        setImageObjectUrl(null);
      }
    } catch {
      if (!hasPlaceholder) setDetail(null);
      if (!cached) setImageObjectUrl(null);
    } finally {
      setDetailLoading(false);
      setDetailApiPending(false);
      setImageLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setImageObjectUrl(null);
    setImageLoading(false);
    setDetailApiPending(false);
  };

  const handleMainSearch = () => {
    if (analyticsSubTab === 'table') {
      void fetchList();
    } else {
      setGallerySearchNonce((n) => n + 1);
    }
  };

  const onGalleryFromToCorrected = (fromStr: string, toStr: string) => {
    setFrom(fromStr);
    setTo(toStr);
  };

  const openDetailFromGallery = (row: AnalyticsGalleryListItem) => {
    void openDetail(row.id, row as AnalyticsListItem);
  };

  const goToRecording = (row: AnalyticsListItem | AnalyticsDetail) => {
    if (row.devicesCameraId == null || row.siteId == null) {
      window.open(recordingsHref, '_blank', 'noopener,noreferrer');
      return;
    }
    const anchorIso =
      typeof row.eventTime === 'string'
        ? row.eventTime
        : new Date(row.eventTime).toISOString();
    const q = new URLSearchParams({
      analytics: '1',
      siteId: String(row.siteId),
      cameraId: String(row.devicesCameraId),
      time: anchorIso,
      /** Playlist corta (min) → menos .ts en NFS/FFmpeg → primer frame más rápido (ajustable 2–20). */
      playbackMin: '3',
    });
    const sep = recordingsHref.includes('?') ? '&' : '?';
    window.open(`${recordingsHref}${sep}${q.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div className="header-title">
          <BarChart3 size={32} className="header-icon" aria-hidden />
          <div>
            <h1>Analíticas de borde</h1>
            <p>Eventos publicados por los appliances XTAM</p>
          </div>
        </div>
      </div>

      <nav className="analytics-subtabs" aria-label="Vista de analíticas">
        <button
          type="button"
          className={`analytics-subtab ${analyticsSubTab === 'gallery' ? 'analytics-subtab--active' : ''}`}
          onClick={() => setAnalyticsSubTab('gallery')}
          aria-current={analyticsSubTab === 'gallery' ? 'page' : undefined}
        >
          <LayoutGrid size={18} aria-hidden />
          Galería
        </button>
        <button
          type="button"
          className={`analytics-subtab ${analyticsSubTab === 'table' ? 'analytics-subtab--active' : ''}`}
          onClick={() => setAnalyticsSubTab('table')}
          aria-current={analyticsSubTab === 'table' ? 'page' : undefined}
        >
          <Table2 size={18} aria-hidden />
          Tabla
        </button>
      </nav>

      <section className="inv-toolbar analytics-toolbar">
        <div className="analytics-toolbar-fields">
          <label className="analytics-field">
            <span>Desde</span>
            <input
              className="inv-input"
              type="datetime-local"
              required
              max={to || undefined}
              value={from}
              onChange={(e) => {
                setPage(1);
                setFrom(e.target.value);
              }}
            />
          </label>
          <label className="analytics-field">
            <span>Hasta</span>
            <input
              className="inv-input"
              type="datetime-local"
              required
              min={from || undefined}
              value={to}
              onChange={(e) => {
                setPage(1);
                setTo(e.target.value);
              }}
            />
          </label>
          <label className="analytics-field analytics-field-grow">
            <span>Sitio</span>
            <input
              className="inv-input"
              type="text"
              placeholder="Nombre del sitio (contiene)"
              value={siteName}
              onChange={(e) => {
                setPage(1);
                setSiteName(e.target.value);
              }}
            />
          </label>
          {analyticsSubTab === 'table' && (
            <label className="analytics-field analytics-field-grow">
              <span>Color vehículo</span>
              <select
                className="inv-select analytics-color-select"
                value={vehicleColorPreset}
                onChange={(e) => {
                  setPage(1);
                  setVehicleColorPreset(e.target.value);
                }}
                aria-label="Filtrar por color de vehículo"
              >
                <option value="">Todos</option>
                <option value="rojo">Rojo</option>
                <option value="negro">Negro</option>
                <option value="azul">Azul</option>
                <option value="amarillo">Amarillo</option>
                <option value="gris">Gris</option>
                <option value="verde">Verde</option>
                <option value="blanco">Blanco</option>
                <option value="morado">Morado</option>
                <option value="naranja">Naranja</option>
                <option value="otro">Otro (no está en la lista)</option>
              </select>
            </label>
          )}
          <button
            type="button"
            className="btn-add analytics-search-btn"
            onClick={() => handleMainSearch()}
            disabled={loading && analyticsSubTab === 'table'}
          >
            <Search size={18} />
            Buscar
          </button>
        </div>
        {analyticsSubTab === 'table' && error && <div className="analytics-error">{error}</div>}
      </section>

      {analyticsSubTab === 'gallery' && (
        <AnalyticsGalleryTab
          from={from}
          to={to}
          siteName={siteName}
          searchNonce={gallerySearchNonce}
          onFromToCorrected={onGalleryFromToCorrected}
          onOpenDetail={openDetailFromGallery}
          goToRecording={(row) => goToRecording(row as unknown as AnalyticsListItem)}
          recordingBlockedReason={(row) =>
            recordingBlockedReason(row as unknown as AnalyticsListItem)
          }
          getCachedEventImageUrl={getCachedEventImageUrl}
          registerEventImageUrl={registerEventImageUrl}
        />
      )}

      {analyticsSubTab === 'table' && (
      <div className="devices-container">
        <div className="table-container">
          <table className="devices-table">
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('event_time')}
                    aria-sort={
                      sortBy === 'event_time'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Hora
                    {sortBy === 'event_time' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('site_name')}
                    aria-sort={
                      sortBy === 'site_name'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Sitio
                    {sortBy === 'site_name' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('camera')}
                    aria-sort={
                      sortBy === 'camera'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Cámara
                    {sortBy === 'camera' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('host_serial')}
                    aria-sort={
                      sortBy === 'host_serial'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Serial
                    {sortBy === 'host_serial' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('persons')}
                    aria-sort={
                      sortBy === 'persons'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Personas
                    {sortBy === 'persons' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('vehicles')}
                    aria-sort={
                      sortBy === 'vehicles'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Vehículos
                    {sortBy === 'vehicles' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="analytics-th-sort"
                    onClick={() => onSortColumn('is_crowd')}
                    aria-sort={
                      sortBy === 'is_crowd'
                        ? sortDir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Aglomeración
                    {sortBy === 'is_crowd' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
                <th className="actions-cell">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="analytics-table-loading-cell">
                    <XTAMSpinner variant="inline" message="Cargando eventos…" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <p>Sin eventos en este rango</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((row) => {
                  const recordingWhy = recordingBlockedReason(row);
                  return (
                    <tr key={row.id}>
                      <td>{formatLocal(row.eventTime)}</td>
                      <td>{row.siteName ?? '—'}</td>
                      <td>{row.cameraName ?? row.cameraExternalId}</td>
                      <td className="analytics-mono">{row.hostSerial}</td>
                      <td>
                        <span className={`analytics-pill analytics-pill--${countTone(row.persons)}`}>
                          {row.persons}
                        </span>
                      </td>
                      <td>
                        <span className={`analytics-pill analytics-pill--${countTone(row.vehicles)}`}>
                          {row.vehicles}
                        </span>
                      </td>
                      <td>{row.isCrowd ? 'Sí' : 'No'}</td>
                      <td className="actions-cell">
                        <div className="table-actions analytics-table-actions">
                          <button type="button" className="analytics-linkish" onClick={() => void openDetail(row.id, row)}>
                            Detalle
                          </button>
                          <button
                            type="button"
                            className="analytics-linkish"
                            onClick={() => goToRecording(row)}
                            disabled={row.devicesCameraId == null || row.siteId == null}
                            title={recordingWhy ?? 'Abrir grabaciones en la fecha del evento'}
                          >
                            <Film size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            Grabación
                          </button>
                          {recordingWhy && (
                            <span className="analytics-recording-hint" title={recordingWhy}>
                              No disponible
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {analyticsSubTab === 'table' && (
      <div className="analytics-pagination">
        <button
          type="button"
          className="btn-refresh"
          disabled={page <= 1 || loading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="analytics-page-meta">
          Página {page} / {totalPages} · {total} eventos
        </span>
        <button
          type="button"
          className="btn-refresh"
          disabled={page >= totalPages || loading}
          onClick={() => setPage((p) => p + 1)}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      )}

      {(detail != null || detailLoading) && (
        <div className="analytics-drawer-backdrop" role="presentation" onClick={closeDetail}>
          <aside
            className="analytics-drawer"
            role="dialog"
            aria-label="Detalle del evento"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="analytics-drawer-head">
              <h2>Evento</h2>
              <button type="button" className="analytics-icon-btn" onClick={closeDetail} aria-label="Cerrar">
                <X size={22} />
              </button>
            </div>
            {detailLoading && !detail && (
              <div className="analytics-drawer-body analytics-drawer-loading">
                <XTAMSpinner variant="inline" message="Cargando detalle…" />
              </div>
            )}
            {detail && (
              <div className="analytics-drawer-body">
                <dl className="analytics-dl">
                  <dt>Hora</dt>
                  <dd>{formatLocal(detail.eventTime)}</dd>
                  <dt>Sitio</dt>
                  <dd>{detail.siteName ?? '—'}</dd>
                  <dt>Cámara</dt>
                  <dd>{detail.cameraName ?? detail.cameraExternalId}</dd>
                  <dt>Serial</dt>
                  <dd className="analytics-mono">{detail.hostSerial}</dd>
                  <dt>Personas / Vehículos</dt>
                  <dd>
                    <span className={`analytics-pill analytics-pill--${countTone(detail.persons)}`}>
                      {detail.persons}
                    </span>{' '}
                    <span className={`analytics-pill analytics-pill--${countTone(detail.vehicles)}`}>
                      {detail.vehicles}
                    </span>
                  </dd>
                </dl>
                {detail.hasImage && (imageLoading || imageObjectUrl) && (
                  <div className="analytics-detail-image">
                    {imageLoading && !imageObjectUrl && (
                      <div className="analytics-detail-image-spinner" aria-hidden>
                        <XTAMSpinner variant="small" message="Cargando imagen…" />
                      </div>
                    )}
                    {imageObjectUrl && (
                      <img src={imageObjectUrl} alt="Captura del evento" decoding="async" />
                    )}
                  </div>
                )}
                {detail.hasImage && !imageObjectUrl && !imageLoading && !detailLoading && (
                  <p className="analytics-muted">Imagen no disponible</p>
                )}
                <h3 className="analytics-detections-title">Detecciones</h3>
                {detailApiPending ? (
                  <div className="analytics-detections-pending" aria-busy="true">
                    <XTAMSpinner variant="inline" message="Cargando detecciones…" />
                  </div>
                ) : (
                  <ul className="analytics-detections">
                    {detail.detections.map((d) => (
                      <li key={d.id}>{formatDetectionForUi(d)}</li>
                    ))}
                  </ul>
                )}
                <div className="analytics-drawer-footer">
                  <button
                    type="button"
                    className="btn-add analytics-drawer-primary"
                    onClick={() => goToRecording(detail)}
                    disabled={detail.devicesCameraId == null || detail.siteId == null}
                  >
                    <Film size={18} />
                    Ver grabación
                  </button>
                  {recordingBlockedReason(detail) ? (
                    <p className="analytics-recording-footnote">{recordingBlockedReason(detail)}</p>
                  ) : null}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
